import axios from 'helpers/axios';
import { log } from 'helpers/debug';
import { parsePath } from 'helpers/parser';
import socket from 'helpers/socket.io';
import { useLocalStorage, useStateRef } from 'hooks';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { FlashContext } from 'providers';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const UserContext = React.createContext();

export const userFields = ['name', 'email', 'phone', 'data', 'roles'].join(',');

const initialState = {
  user: {},
  isLoggedIn: false,
  isAdmin: false,
  challenges: {
    required: false,
    valid: false,
  },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SAVE_USER': {
      const { user, isLoggedIn = true } = action;
      return {
        ...state,
        user,
        isLoggedIn,
        isAdmin: user?.roles?.includes('admin'),
      };
    }
    case 'REMOVE_USER':
      return {
        ...state,
        user: {},
        isLoggedIn: false,
        isAdmin: false,
        challenges: {
          required: false,
          valid: false,
        },
      };
    case 'SAVE_CHALLENGES': {
      const challenges = { ...state.challenges, ...action.challenges };
      return {
        ...state,
        challenges,
        isLoggedIn: challenges.valid,
      };
    }
    case 'SAVE_SETTINGS':
      return {
        ...state,
        user: {
          ...state.user,
          data: {
            ...state.user.data,
            settings: _.merge({}, state.user.data?.settings, action.settings),
          },
        },
      };
    case 'SAVE_SESSION':
      return {
        ...state,
        user: {
          ...state.user,
          session: _.merge({}, state.user.session, action.session),
        },
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export const UserProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [session, setSession, clearSession] = useLocalStorage('session', null);
  const [, , clearReportsDate] = useLocalStorage('reportsDate');
  const history = useHistory();
  const location = useLocation();
  const { setFlash } = React.useContext(FlashContext);
  // using a Ref instead of "state.isAdmin" directly
  // because state needs a re-render to be applied, while Ref doesn't need to
  const isAdmin = useStateRef(state.isAdmin, false);

  React.useEffect(() => {
    const { isLoggedIn, user } = state;
    log(`session: ${session}, isLoggedIn: ${isLoggedIn}, user: ${user?.id}`);
    if (!session && isLoggedIn) {
      // handle signout over all opened browser tabs
      setFlash({
        title: 'Disconnected',
        message: 'You have been disconnected.',
        options: {
          adjustPosition: false,
          closeOnOutsideClick: false,
          refreshOnClose: true,
        },
      });
      removeUser();
      redirectToLogin();
    } else if (session && isLoggedIn && session !== user?.id) {
      // handle user change (due to sign in on another tab)
      setFlash({
        title: 'Disconnected',
        message: 'User has changed. Please reload the current tab before continuing.',
        options: {
          closeOnOutsideClick: false,
          refreshOnClose: true,
        },
      });
    }
  }, [session]);

  const saveUser = (user, isLoggedIn) => dispatch({ type: 'SAVE_USER', user, isLoggedIn });

  const removeUser = () => dispatch({ type: 'REMOVE_USER' });

  const saveSettings = async (settings, persist = false) => {
    let proceed = !persist || false;
    if (persist) {
      try {
        const response = await axios.post('/me/?$merge=true', { data: { settings } });
        if (response.status === 200) {
          proceed = true;
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (proceed) {
      dispatch({ type: 'SAVE_SETTINGS', settings });
    }
  };

  const saveChallenges = (challenges) => dispatch({ type: 'SAVE_CHALLENGES', challenges });

  const saveSession = async (session, persist = false) => {
    let proceed = !persist || false;
    if (persist) {
      try {
        const response = await axios.put('/uext/session/', session);
        if (response.status === 200) {
          proceed = true;
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (proceed) {
      dispatch({ type: 'SAVE_SESSION', session });
    }
  };

  const fetchUser = async (redirectOnSuccess = true) => {
    try {
      const response = await axios.get(`/me/?$select=${userFields}&$sess=true`);
      if (response.status === 200 && response.data) {
        // save user
        saveUser(response.data, false);
        // check challenges
        const challenges = response.data.session?.challenges || [];
        const requiredChallenges = challenges.filter(({ valid }) => !valid);
        const validatedChallenges = challenges.filter(({ valid }) => valid).map(({ type }) => type);
        if (requiredChallenges.length > 0 && !validatedChallenges.includes('sms')) {
          // challenges not resolved
          saveChallenges({ required: true, valid: false });
          displayChallenges(requiredChallenges);
        } else {
          // challenges resolved
          saveChallenges({ valid: true });
          // redirect
          if (redirectOnSuccess) {
            return redirect(true);
          }
          return response.data;
        }
      } else {
        redirect();
      }
    } catch (err) {
      console.error(err);
      redirect();
    }
  };

  const displayChallenges = (challenges) => {
    challenges.forEach((challenge) => {
      switch (challenge.type) {
        case 'otp': {
          const path =
            challenge.initialised && challenge.tries?.succeeded > 0 ? 'login' : 'get-started';
          redirectTo(`/2fa/${path}`);
          return;
        }
        case 'sms':
          // not implemented here to force users to use OTP
          return;
        default:
          console.error(`Unknown challenge type: ${challenge.type}.`);
          break;
      }
    });
  };

  const signIn = async (username, password, throwErrors = false) => {
    try {
      const response = await axios.post(`/uext/auth/signin?$select=${userFields}`, {
        username,
        password,
      });
      if (response.status === 200) {
        setSession(response.data?.id);
        socket.reconnect(); // reconnect to websockets as an authenticated user
        return true;
      }
    } catch (err) {
      if (throwErrors) {
        throw err;
      } else {
        console.error(err);
      }
    }
    return false;
  };

  const signOut = async () => {
    try {
      const response = await axios.get('/auth/signout');
      if (response.status === 200) {
        removeUser();
        clearSession(true);
        clearReportsDate();
        socket.reconnect(); // reconnect to websockets as guest
        redirectToLogin();
        return true;
      }
    } catch (err) {
      console.error(err);
    }
    return false;
  };

  const redirect = (isLoggedIn = false) => {
    const path = parsePath(location.pathname);
    if (isLoggedIn) {
      // redirection for logged in users
      switch (path.prefix) {
        case '/sign-in':
        case '/sign-up':
        case '/forgot-password':
        case '/reset-password':
          redirectToDashboard();
          break;
        case '/2fa':
          switch (path.suffix) {
            case '/reset':
              break;
            default:
              redirectToDashboard();
              break;
          }
          break;
        default:
          break;
      }
    } else {
      // redirection for non logged in users
      switch (path.prefix) {
        case '/sign-up':
        case '/forgot-password':
        case '/reset-password':
          break;
        case '/2fa':
        default:
          redirectToLogin();
          break;
      }
    }
  };

  const redirectTo = (path, method = 'push') => {
    const [pathname, search] = path.split('?');
    history[method]({ pathname, search });
  };

  const redirectToLogin = (method = 'push') => {
    history[method]({ pathname: '/sign-in' });
  };

  const redirectToDashboard = (method = 'push') => {
    const pathname = isAdmin.current ? '/admin/dashboard' : '/dashboard';
    history[method]({ pathname });
  };

  return (
    <UserContext.Provider
      value={{
        state,
        dispatch,
        signIn,
        signOut,
        fetchUser,
        saveUser,
        removeUser,
        saveSettings,
        saveChallenges,
        saveSession,
        redirectTo,
        redirectToLogin,
        redirectToDashboard,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
